import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiTrendingUp, FiUsers, FiCheckCircle, FiClock, FiDollarSign, FiZap, FiSmartphone } = FiIcons;

const Home = () => {
  const onlineBenefits = [
    { icon: FiClock, title: 'Instant Quotes', description: 'Get your quote in under 5 minutes - no waiting, no appointments needed.' },
    { icon: FiZap, title: 'Same Day Coverage', description: 'Purchase and activate your insurance policy immediately online.' },
    { icon: FiSmartphone, title: 'No Agents Required', description: 'Complete the entire process online at your convenience, 24/7.' },
    { icon: FiDollarSign, title: 'Transparent Pricing', description: 'See exact costs upfront with no hidden fees or surprise charges.' }
  ];

  const features = [
    { icon: FiShield, title: 'Comprehensive Coverage', description: 'From general liability to workers comp, we have all your business insurance needs covered.' },
    { icon: FiUsers, title: 'California Expertise', description: 'Licensed professionals who understand California business insurance requirements.' },
    { icon: FiCheckCircle, title: 'Trusted Partnership', description: 'Backed by Coterie Insurance with A-rated financial strength and stability.' },
    { icon: FiTrendingUp, title: 'Modern Technology', description: 'Cutting-edge platform that makes buying business insurance simple and fast.' }
  ];

  const insuranceTypes = [
    { title: 'Business Owners Policy (BOP)', description: 'Package policy combining property, business interruption, and liability insurance in one convenient bundle.', link: '/business-owners-policy' },
    { title: 'General Liability Insurance', description: 'Protect your business from third-party claims of bodily injury, property damage, and advertising injury.', link: '/general-liability' },
    { title: 'Professional Liability Insurance', description: 'Coverage for claims of professional negligence, errors, and omissions in your services.', link: '/professional-liability' },
    { title: 'Cyber Insurance', description: 'Protection against cyber threats including data breaches, ransomware, and system attacks.', link: '/cyber-insurance' },
    { title: 'Workers Compensation', description: 'Required coverage for employee injuries and illnesses that occur on the job.', link: '/workers-compensation' },
    { title: 'Workplace Violence Coverage', description: 'Specialized coverage for recovery from violent workplace incidents with crisis management support.', link: '/workplace-violence' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-blue-900 bg-opacity-80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Business Insurance Made Simple
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto"
            >
              Skip the agents, skip the paperwork. Get instant quotes and same-day coverage for your California business through our digital insurance platform.
            </motion.p>

            {/* Online Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8"
            >
              {onlineBenefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <SafeIcon icon={benefit.icon} className="h-8 w-8 text-orange-400 mx-auto mb-2" />
                  <h3 className="font-semibold text-sm">{benefit.title}</h3>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center"
            >
              <a
                href="https://coterie.com/quote"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
              >
                Get My Price
              </a>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-blue-200 text-sm mt-4"
            >
              No obligation • No agents required • Instant coverage available
            </motion.p>
          </div>
        </div>
      </section>

      {/* Online Benefits Detail Section */}
      <section className="py-20 bg-gradient-to-r from-orange-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Buy Insurance Online?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the future of business insurance with our streamlined digital process.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {onlineBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Burbank Insurance + Coterie?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine local expertise with innovative insurance technology to deliver the best coverage for your California business.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={feature.icon} className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Types Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Business Insurance Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protect your California business with our full range of coverage options.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {insuranceTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl hover:shadow-lg transition-shadow"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{type.title}</h3>
                <p className="text-gray-600 mb-6">{type.description}</p>
                <Link
                  to={type.link}
                  className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                >
                  Learn More
                  <SafeIcon icon={FiTrendingUp} className="ml-2 h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Covered in Minutes?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of California businesses that chose the modern way to buy insurance. No agents, no waiting, no hassle.
          </p>
          <div className="flex justify-center">
            <a
              href="https://coterie.com/quote"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
            >
              Get My Price Now
            </a>
          </div>
          <p className="text-blue-200 text-sm mt-4">
            CA License #6013543 • Same day coverage available
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;